import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  Avatar,
  Boton,
  EsqueletoTabla,
  Input,
  Pill,
  Tabla,
  TablaCabecera,
  TablaCelda,
  TablaCuerpo,
  TablaEncabezado,
  TablaFila,
  TituloPagina,
  Vacio,
  cn,
} from "@iavmx/ui";
import { Chip } from "../componentes";
import { leer } from "../api";
import { useSesion } from "../sesion";
import type { Area, PersonaDirectorio } from "../tipos";
import { HERRAMIENTAS } from "../../compartido/apps";

type Datos = { areas: Area[]; personas: PersonaDirectorio[] };

function normalizar(t: string) {
  return t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export function Directorio() {
  const { api, yo } = useSesion();
  const [busqueda, setBusqueda] = React.useState("");
  const [areaSel, setAreaSel] = React.useState<string | null>(null);
  const [appSel, setAppSel] = React.useState<string | null>(null);
  const q = useQuery({
    queryKey: ["directorio"],
    queryFn: () => api.api.directorio.$get().then((r) => leer<Datos>(r)),
  });
  if (q.isLoading) return <EsqueletoTabla />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message} />;
  const d = q.data;
  const nombreArea = new Map(d.areas.map((a) => [a.id, a.nombre]));
  const conteoArea = new Map<string, number>();
  for (const p of d.personas) if (p.areaId) conteoArea.set(p.areaId, (conteoArea.get(p.areaId) ?? 0) + 1);
  const areasConGente = d.areas.filter((a) => (conteoArea.get(a.id) ?? 0) > 0);
  const miArea = yo?.areaId ?? null;
  areasConGente.sort((a, b) => (a.id === miArea ? -1 : b.id === miArea ? 1 : a.nombre.localeCompare(b.nombre, "es")));
  const apps = HERRAMIENTAS.filter((h) => h.disponible && (h.clave !== "semaforo"));
  const conteoApp = (app: string) => d.personas.filter((p) => p.accesos.some((a) => a.app === app)).length;

  const termino = normalizar(busqueda.trim());
  const filtradas = d.personas.filter((p) => {
    if (areaSel && p.areaId !== areaSel) return false;
    if (appSel && !p.accesos.some((a) => a.app === appSel)) return false;
    if (!termino) return true;
    const pajar = normalizar([p.nombre, p.correo, p.puesto ?? "", nombreArea.get(p.areaId ?? "") ?? "", p.jefeNombre ?? "", p.comoRecibirEncargos ?? "", ...p.accesos.map((a) => HERRAMIENTAS.find((h) => h.app === a.app)?.nombre ?? a.app)].join(" "));
    return termino.split(/\s+/).every((t) => pajar.includes(t));
  });
  const ordenadas = [...filtradas].sort((a, b) => {
    const ia = areasConGente.findIndex((x) => x.id === a.areaId);
    const ib = areasConGente.findIndex((x) => x.id === b.areaId);
    return ia - ib || a.nombre.localeCompare(b.nombre, "es");
  });

  return (
    <>
      <TituloPagina
        descripcion={`${d.personas.length} personas en ${areasConGente.length} áreas · quién es jefe de qué y cómo prefiere que le pidas las cosas`}
        accion={
          <Boton variante="contorno" asChild><Link to="/ajustes">Actualizar mi ficha</Link></Boton>
        }
      >
        Directorio
      </TituloPagina>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="relative w-full sm:w-80">
          <span className="sr-only">Buscar por nombre, área o herramienta</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-tenue" aria-hidden="true" />
          <Input type="search" placeholder="Buscar por nombre, área o herramienta" className="h-11 pl-9 text-base" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </label>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar por herramienta">
          <Chip activo={appSel === null} onClick={() => setAppSel(null)} n={d.personas.length}>Todas</Chip>
          {apps.map((h) => (
            <Chip key={h.app} activo={appSel === h.app} onClick={() => setAppSel(appSel === h.app ? null : h.app)} n={conteoApp(h.app)}>
              {h.app === "coordinacion" ? "360 y Semáforo" : h.nombre}
            </Chip>
          ))}
        </div>
      </div>
      <p className="mb-4 font-ui text-sm text-tinta-tenue" aria-live="polite">
        {filtradas.length === d.personas.length ? `${filtradas.length} personas · tu área primero` : `${filtradas.length} de ${d.personas.length} personas`}
      </p>
      <div className="grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
        <nav aria-label="Áreas y responsables" className="lg:sticky lg:top-4 lg:self-start">
          <p className="mb-1 font-ui text-xs font-semibold uppercase tracking-wide text-tinta-tenue">Áreas y responsables</p>
          <ul className="divide-y divide-linea border-y border-linea lg:border-y-0">
            <li>
              <button type="button" onClick={() => setAreaSel(null)} aria-pressed={areaSel === null} className={cn("flex w-full min-h-11 items-center justify-between px-2 text-left font-ui text-sm hover:bg-fondo-suave", areaSel === null && "bg-fondo-suave font-semibold")}>
                Todas las áreas
              </button>
            </li>
            {areasConGente.map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => setAreaSel(areaSel === a.id ? null : a.id)} aria-pressed={areaSel === a.id} className={cn("flex w-full min-h-11 items-center justify-between gap-2 px-2 py-1.5 text-left font-ui text-sm hover:bg-fondo-suave", areaSel === a.id && "bg-fondo-suave")}>
                  <span>
                    <span className="block font-semibold">{a.nombre}{a.id === miArea && <span className="font-normal text-tinta-tenue"> · tu área</span>}</span>
                    <span className="block text-xs text-tinta-tenue">{a.jefeNombre ?? "Sin responsable asignado"}</span>
                  </span>
                  <span className="text-tinta-tenue">{conteoArea.get(a.id)}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        {ordenadas.length === 0 ? (
          <Vacio titulo="Sin coincidencias" texto="Prueba con otro nombre o quita un filtro." accion={<Boton variante="contorno" tamano="sm" onClick={() => { setBusqueda(""); setAreaSel(null); setAppSel(null); }}>Quitar filtros</Boton>} />
        ) : (
          <Tabla>
            <TablaCabecera>
              <TablaFila>
                <TablaEncabezado>Persona</TablaEncabezado>
                <TablaEncabezado>Área</TablaEncabezado>
                <TablaEncabezado>Jefe directo</TablaEncabezado>
                <TablaEncabezado>Herramientas</TablaEncabezado>
                <TablaEncabezado>Prefiere recibir encargos</TablaEncabezado>
              </TablaFila>
            </TablaCabecera>
            <TablaCuerpo>
              {ordenadas.map((p) => {
                const area = d.areas.find((a) => a.id === p.areaId);
                const esJefe = area?.jefeId === p.id;
                const soyYo = p.id === yo?.id;
                return (
                  <TablaFila key={p.id} className={cn(soyYo && "bg-fondo-suave")}>
                    <TablaCelda>
                      <span className="flex items-center gap-2.5">
                        <Avatar nombre={p.nombre} src={p.fotoUrl ?? p.avatarUrl} className="size-8" />
                        <span className="min-w-0">
                          <span className="block font-semibold">{p.nombre}{soyYo && <span className="font-normal text-tinta-tenue"> · tú</span>}</span>
                          <span className="block text-xs text-tinta-tenue">{[p.puesto, p.correo].filter(Boolean).join(" · ")}</span>
                        </span>
                      </span>
                    </TablaCelda>
                    <TablaCelda>{area?.nombre ?? "—"}{esJefe && <Pill tono="tinta" className="ml-2">Jefatura</Pill>}</TablaCelda>
                    <TablaCelda className={cn(!p.jefeNombre && "text-tinta-tenue")}>{p.jefeNombre ?? "—"}</TablaCelda>
                    <TablaCelda>
                      <span className="flex flex-wrap gap-1.5">
                        {p.rol === "admin" && <Pill tono="tinta">Admin</Pill>}
                        {p.accesos.map((a) => (
                          <Pill key={a.app} tono="neutro">{a.app === "coordinacion" ? "360 · Semáforo" : (HERRAMIENTAS.find((h) => h.app === a.app)?.nombre ?? a.app)}</Pill>
                        ))}
                        {p.accesos.length === 0 && p.rol !== "admin" && <span className="text-tinta-tenue">—</span>}
                      </span>
                    </TablaCelda>
                    <TablaCelda className={cn("max-w-xs", !p.comoRecibirEncargos && "text-tinta-tenue")}>
                      {p.comoRecibirEncargos ?? { correo: "Por correo", whatsapp: "Por WhatsApp", formulario: "Por formulario", slack: "Por Slack" }[p.canalPreferido] ?? "—"}
                    </TablaCelda>
                  </TablaFila>
                );
              })}
            </TablaCuerpo>
          </Tabla>
        )}
      </div>
      <p className="mt-4 font-ui text-xs text-tinta-tenue">Cada quien edita su propia ficha en Ajustes; áreas y jefes los cambia administración.</p>
    </>
  );
}
